import { z } from 'zod';

import { shared } from '@tig-dpqa-cloud/contract-backend-common';
import { getPagedResultSchema, getQuerySchema } from '@tig-dpqa-cloud/util-sfp';
import { isBefore } from 'date-fns';

export const SlideScansListResponse = getPagedResultSchema(shared.SlideScanItem).extend(
  z.object({ credentials: shared.BucketCredentials }).shape,
);

export type SlideScansListResponseType = z.infer<typeof SlideScansListResponse>;

export const dateString = z.iso.datetime({ offset: true }).pipe(z.coerce.date());

export const numberString = z.string().pipe(z.coerce.number());

export const tagOptions = z.enum([
  ...([
    'WITHOUT_ISSUES',
    'PROCESSING_FAILED',
    'RESCAN',
    'REJECTED_ISSUES',
    'ACCEPTED_ISSUES',
    'USER_DETECTED_ISSUES',
    'TO_BE_RESCANNED',
    'VERIFIED_AUTOMATED',
    'VERIFIED_MANUAL',
    'VERIFIED_NOT_SET',
  ] as const),
  ...shared.SlideScanIssue.options,
]);

export type TagOptionsType = z.infer<typeof tagOptions>;

export const SlideScanListQueryParams = getQuerySchema(
  z.object({
    triggeredAt: z.iso.datetime({ offset: true })
      .describe('The date, time and timezone the slide scan was triggered'),
    status: shared.SlideScanGeneralQcStatus.describe('The slide scan status you want to see, can be an array'),
    scannerType: z.string(),
    scannerSerial: z.string(),
    rackNumber: z.number(),
    search: z.string(),
    staining: z.string().or(z.array(z.string())),
    tags: z
      .array(tagOptions)
      .describe('Tags related to a slide scan, this filter is recommended with the in combinator'),
    customField1: z.string(),
    physicalId: z.string().describe('The physical ID of the slide'),
  }),
).partial();

export type SlideScanListQueryParamsType = z.infer<typeof SlideScanListQueryParams>;

export const SlideScanListQueryableParams = SlideScanListQueryParams.pick({
  triggeredAt: true,
  status: true,
  scannerType: true,
  scannerSerial: true,
  rackNumber: true,
  search: true,
  staining: true,
  tags: true,
  customField1: true,
  physicalId: true,
}).keyof();

export type SlideScanListQueryableParamsType = z.infer<typeof SlideScanListQueryableParams>;

export const dateRange = z.object({
  start: dateString.describe('Start date to get slide scans'),
  end: dateString.describe('End date to get slide scans'),
});
export const dashboardParams = dateRange.extend({
  scannerType: z.string().optional(),
  scannerSerial: z.string().optional(),
});

export const dashboardParamsWithDateRangeCheck = dashboardParams.refine(
  ({ start, end }) =>
    isBefore(start, end),
  {
    error: 'Start date should be before the end date',
  });

export const SlideScansListQueryParameters = dateRange.partial().and(
  z.object({
    page: numberString.pipe(z.int().min(1)).optional().describe('No of slide scans to be returned'),
    pagesize: numberString
      .pipe(z.int().positive().max(shared.CONSTANTS.MAX_PAGE_SIZE))
      .optional()
      .describe('Offset for the result'),
  }),
);
